import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationCreatePlayerTag from '../../../../api/hooks/useMutationCreatePlayerTag'
import { TagsQueryKeys } from '../../../../constants'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { PresetPriorityHint } from '@/common/components/PresetPriorityHint'

const createPlayerTagSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อแท็ก'),
	color: z
		.string()
		.regex(/^#[0-9A-F]{6}$/i, 'กรุณากรอกรหัสสีที่ถูกต้อง (เช่น #FFD700)'),
	autoTagCondition: z.enum(['none', 'minDepositAmount', 'depositGtWithdraw']),
	minDepositAmount: z.string().optional(),
	depositGtWithdrawAmount: z.string().optional(),
	presetId: z.string().optional(),
	presetNumber: z.string().optional(),
})

type CreatePlayerTagFormValues = z.infer<typeof createPlayerTagSchema>

interface CreatePlayerTagModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export default function CreatePlayerTagModal({
	open,
	onOpenChange,
}: CreatePlayerTagModalProps) {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)

	const { data: presets } = useQueryGetPresetId()

	const form = useForm<CreatePlayerTagFormValues>({
		resolver: zodResolver(createPlayerTagSchema),
		defaultValues: {
			name: '',
			color: '#6B7280',
			autoTagCondition: 'none',
			minDepositAmount: '',
			presetId: '',
			presetNumber: '',
		},
	})

	const autoTagCondition = form.watch('autoTagCondition')

	const { mutateAsync: createTag } = useMutationCreatePlayerTag()

	const onSubmit = async (values: CreatePlayerTagFormValues) => {
		try {
			setIsLoading(true)

			const payload = {
				name: values.name,
				color: values.color,
				...(values.autoTagCondition === 'minDepositAmount' &&
					values.minDepositAmount && {
						minDepositAmount: parseFloat(values.minDepositAmount),
					}),
				...(values.autoTagCondition === 'depositGtWithdraw' &&
					values.depositGtWithdrawAmount && {
						depositGtWithdraw: parseFloat(values.depositGtWithdrawAmount),
					}),
				presetId: values.presetId || null,
				presetNumber: values.presetNumber
					? parseInt(values.presetNumber)
					: null,
			}

			await createTag({ payload })

			toast.success('สร้างแท็กสำเร็จ')

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTagStatistics],
			})
			queryClient.invalidateQueries({
				queryKey: [PlayerQueryKeys.GetPlayerList],
			})

			form.reset()
			onOpenChange(false)
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>เพิ่มแท็กใหม่</DialogTitle>
					<DialogDescription>
						สร้างแท็กใหม่สำหรับจัดกลุ่มผู้เล่น
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ชื่อแท็ก</FormLabel>
									<FormControl>
										<Input placeholder="เช่น VIP 50K+" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="color"
							render={({ field }) => (
								<FormItem>
									<FormLabel>สีแท็ก</FormLabel>
									<div className="flex gap-2">
										<FormControl>
											<Input type="color" className="h-10 w-20" {...field} />
										</FormControl>
										<FormControl>
											<Input placeholder="#FFD700" {...field} />
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="autoTagCondition"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เงื่อนไขแท็กอัตโนมัติ</FormLabel>
									<Select
										onValueChange={field.onChange}
										defaultValue={field.value}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกเงื่อนไข" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value="none">ไม่ใช้แท็กอัตโนมัติ</SelectItem>
											<SelectItem value="minDepositAmount">
												ยอดฝากขั้นต่ำ
											</SelectItem>
											<SelectItem value="depositGtWithdraw">
												ยอดฝาก {'>'} ยอดถอน
											</SelectItem>
										</SelectContent>
									</Select>
									<FormDescription>
										เลือกเงื่อนไขสำหรับแท็กอัตโนมัติ
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						{autoTagCondition === 'minDepositAmount' && (
							<FormField
								control={form.control}
								name="minDepositAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ยอดฝากขั้นต่ำ (บาท)</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="เช่น 50000"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											ผู้เล่นที่มียอดฝากรวมมากกว่าหรือเท่ากับจำนวนนี้จะได้รับแท็กอัตโนมัติ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{autoTagCondition === 'depositGtWithdraw' && (
							<FormField
								control={form.control}
								name="depositGtWithdrawAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											จำนวนเงินขั้นต่ำที่ฝากมากกว่าถอน (บาท)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="เช่น 10000"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											ผู้เล่นที่มี (ยอดฝาก - ยอดถอน)
											มากกว่าจำนวนนี้จะได้รับแท็กอัตโนมัติ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						<div className="space-y-4 rounded-lg border p-4">
							<div className="text-sm font-medium">การตั้งค่า RTP</div>
							<PresetPriorityHint where="tag" className="w-full" />

							<FormField
								control={form.control}
								name="presetId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>RTP Preset ID</FormLabel>
										<FormDescription>
											สำหรับ PGHARD และ JOKERHARD
										</FormDescription>
										<FormControl>
											<Select
												value={field.value || undefined}
												onValueChange={field.onChange}
											>
												<SelectTrigger>
													<SelectValue placeholder="เลือก RTP Preset" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="">ไม่ใช้ RTP Preset</SelectItem>
													{presets?.presets.map((preset) => (
														<SelectItem key={preset.id} value={preset.id}>
															{preset.note}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="presetNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>RTP Preset Number (เปอร์เซ็นต์ RTP)</FormLabel>
										<FormDescription>
											สำหรับ Provider อื่นๆ (ค่า 30-99)
										</FormDescription>
										<FormControl>
											<Input
												type="number"
												min={30}
												max={99}
												placeholder="เช่น 95"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
								disabled={isLoading}
							>
								ยกเลิก
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? 'กำลังสร้าง...' : 'สร้างแท็ก'}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
